import {createEnvironmentBoundTask, type EnvironmentBoundTask} from './environment-task'
import {AuthToken} from '@github-ui/copilot-auth-token/auth-token'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
// eslint-disable-next-line no-restricted-imports -- Speculative failures must be reported without interrupting submission.
import {reportError} from '@github-ui/failbot'
import type {NewSessionProvisioningContext} from '@github-ui/one-copilot-composer/new'
import {earlyTaskVisibilityScope, hideEarlyTask, publishEarlyTask} from './early-task-visibility'

type ProvisioningScope = Pick<NewSessionProvisioningContext, 'scope' | 'space' | 'repository' | 'branch'>

interface Attempt {
  visibilityScope: string
  configurationKey: string
  repositoryBound: boolean
  scopeKey: string
  result?: EnvironmentBoundTask
  discarded: boolean
  accepted: boolean
  adopted: boolean
}

function scopeKey(context: ProvisioningScope): string {
  return JSON.stringify([
    context.repository ? undefined : context.scope?.trim(),
    context.space?.owner,
    context.space?.slug,
    context.repository,
  ])
}

function configurationKey(context: NewSessionProvisioningContext): string {
  return JSON.stringify([scopeKey(context), context.target, context.environmentId])
}

export class EarlyTaskProvisioning {
  #attempt?: Attempt
  #contextKey?: string
  #submitting = false
  readonly #authToken?: AuthToken
  readonly #apiURL?: string
  constructor(apiURL?: string, githubToken?: string) {
    this.#apiURL = apiURL
    this.#authToken = githubToken ? new AuthToken(githubToken, '2999-01-01T00:00:00Z', []) : undefined
  }

  onInteraction = (context: NewSessionProvisioningContext): void => {
    this.onContextChange(context)
    if (context.target !== 'chat' || !context.scope?.trim() || context.repository) return
    this.#start(context)
  }

  onRepositoryInteraction = (context: NewSessionProvisioningContext): void => {
    this.onContextChange(context)
    if (context.target !== 'task' || !context.repository || !context.branch) return
    this.#start(context)
  }

  #start(context: NewSessionProvisioningContext): void {
    if (
      this.#attempt ||
      this.#submitting ||
      !copilotFeatureFlags.earlyTaskProvisioning ||
      context.environmentId !== 'github-sandbox'
    ) {
      return
    }
    const attempt: Attempt = {
      visibilityScope: earlyTaskVisibilityScope(),
      configurationKey: configurationKey(context),
      repositoryBound: Boolean(context.repository),
      scopeKey: scopeKey(context),
      discarded: false,
      accepted: false,
      adopted: false,
    }
    this.#attempt = attempt
    performance.mark('copilot-early-task:start')
    void this.#provision(attempt, context)
  }

  onContextChange = (context: NewSessionProvisioningContext): void => {
    this.#contextKey = configurationKey(context)
    if (
      this.#attempt &&
      ((this.#attempt.repositoryBound && this.#attempt.configurationKey !== this.#contextKey) ||
        this.#attempt.visibilityScope !== earlyTaskVisibilityScope())
    ) {
      this.#discard()
    }
  }

  onBeforeSubmit = (context?: NewSessionProvisioningContext): void => {
    if (this.#submitting) return
    this.#submitting = true
    const attempt = this.#attempt
    if (attempt) performance.mark('copilot-early-task:submit')
    // Freeze compatibility at Send, before prompt preparation or an accepted handoff can unmount the composer.
    if (
      !attempt?.result ||
      attempt.configurationKey !== (context ? configurationKey(context) : this.#contextKey) ||
      attempt.visibilityScope !== earlyTaskVisibilityScope()
    ) {
      this.#discard()
    }
  }

  accept(context: ProvisioningScope): void {
    this.onBeforeSubmit()
    const attempt = this.#attempt
    if (!attempt || attempt.discarded) return
    if (attempt.scopeKey !== scopeKey(context) || attempt.visibilityScope !== earlyTaskVisibilityScope()) {
      this.#discard()
      return
    }
    attempt.accepted = true
  }

  take(context: ProvisioningScope): EnvironmentBoundTask | undefined {
    this.accept(context)
    const attempt = this.#attempt
    if (!attempt || attempt.discarded || attempt.adopted) return undefined
    const result = attempt.result
    attempt.result = undefined
    attempt.adopted = true
    if (result) publishEarlyTask(result.taskId, attempt.visibilityScope)
    performance.mark('copilot-early-task:adopted')
    return result
  }

  onDispose = (): void => {
    // An accepted handoff unmounts the composer before the shell provisions.
    if (!this.#attempt?.accepted) this.#discard()
  }

  onAbandonSubmission = (): void => {
    this.#discard()
  }

  reset(): void {
    this.#discard()
    this.#attempt = undefined
    this.#contextKey = undefined
    this.#submitting = false
  }

  async #provision(attempt: Attempt, context: NewSessionProvisioningContext): Promise<void> {
    let allocatedTaskId: string | undefined
    try {
      const result = await createEnvironmentBoundTask(
        {
          copilotApiUrl: this.#apiURL,
          authToken: this.#authToken,
          environmentId: 'github-sandbox',
          problemStatement: '',
          ...(context.repository
            ? {repositoryNwo: context.repository, baseRef: context.branch}
            : {computeScope: context.scope}),
          space: context.space,
        },
        taskId => {
          allocatedTaskId = taskId
          hideEarlyTask(taskId, attempt.visibilityScope)
        },
      )
      if (attempt.discarded) {
        void this.#deleteTask(result.taskId)
      } else {
        attempt.result = result
        performance.mark('copilot-early-task:ready')
      }
    } catch (error) {
      attempt.discarded = true
      reportError(error, {message: 'Early task provisioning failed'})
      if (allocatedTaskId) void this.#deleteTask(allocatedTaskId)
    }
  }

  #discard(): void {
    const attempt = this.#attempt
    if (!attempt || attempt.discarded || attempt.adopted) return
    attempt.discarded = true
    if (attempt.result) {
      void this.#deleteTask(attempt.result.taskId)
      attempt.result = undefined
    }
  }

  async #deleteTask(taskId: string): Promise<void> {
    try {
      await fetchCAPI({
        copilotApiUrl: this.#apiURL,
        authToken: this.#authToken,
        path: `/agents/tasks/${encodeURIComponent(taskId)}`,
        method: 'DELETE',
      })
    } catch (error) {
      reportError(error, {message: 'Failed to delete unused early task'})
    }
  }
}
